var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { Component, Watch } from 'vue-property-decorator';
import PageHeader from '@/components/page-header/PageHeader';
import { dayMap, businessHours, defaultCalendarSettings, } from '@/support/CalendarDefaults';
import { parseTimeslotsToCalendarEvents, parseToTimeslotModel, } from '@/support/CalendarEventFunctions';
import { RosterTemplate as RosterTemplateModel } from '@/models/RosterTemplate';
import ErrorHandler from '@/support/ErrorHandler';
import ShowActivitiesDialog from './ShowActivitiesDialog';
import AddTimeslotDialog from '@/views/RosterTemplates/AddTimeslotDialog';
import { DateTime } from 'luxon';
import { Model } from '@/models/Model';
import LoaderCard from '@/components/loader-card/LoaderCard';
import AsyncSelect from '@/components/async-select/AsyncSelect';
import { Subject } from '@/models/Subject';
import { Room } from '@/models/Room';
import { Teacher } from '@/models/Teacher';
import { Level } from '@/models/Level';
import { Group } from '@/models/Group';
import { renderCustomTimeslot } from '@/support/CalendarEventRenderFunctions';
import { isArray, isEmpty } from 'lodash';
import AbstractUrl from '@/support/AbstractUrl';
import ScoolSnackbar from '@/components/ScoolSnackbar/ScoolSnackbar';
import CreateActivityDialog from './CreateActivityDialog';
import CreateRecessDialog from './CreateRecessDialog';
let RosterTemplate = class RosterTemplate extends AbstractUrl {
    constructor() {
        super(...arguments);
        this.isAddingMultipleEvents = false;
        this.isShowingAddBreakDialog = false;
        this.isShowingCreateActivitiesDialog = false;
        this.selectedTimeslots = [];
        this.eventData = null;
        this.rosterTemplate = null;
        this.isShowingTemplateDialog = false;
        this.isShowingActivitiesDialog = false;
        this.isAddingTimeslot = false;
        this.isEditingTitle = false;
        this.isUpdated = false;
        this.isLoading = true;
        this.clearSelects = false;
        this.disabled = false;
        this.hasWeekends = false;
        this.backgroundEvents = [];
        this.events = [];
        this.calendarRendered = false;
        this.calendarView = null;
        this.selectedEventId = null;
        this.datesRenderTimeout = null;
        this.calendarLoaded = false;
        this.newTitle = '';
        this.oldTitleValue = null;
        this.subjectModel = new Subject();
        this.areas = [];
        this.roomModel = new Room();
        this.rooms = [];
        this.teacherModel = new Teacher();
        this.users = [];
        this.levelModel = new Level();
        this.levels = [];
        this.groupModel = new Group().sort('name');
        this.groups = [];
        this.activitiesString = null;
    }
    toggleEvent() {
        this.isAddingMultipleEvents = !this.isAddingMultipleEvents;
    }
    toggleSelectedEvent(event) {
        if (event.el.classList.contains('selected-event')) {
            this.removeSelectedEvent(event);
        }
        else {
            this.addSelectedEvent(event);
        }
    }
    clearSelectedTimeslots() {
        const selectedEvents = Array.from(document.getElementsByClassName('selected-event'));
        selectedEvents.forEach((element) => element.classList.remove('selected-event'));
        this.selectedTimeslots = [];
    }
    removeSelectedEvent(event) {
        this.selectedTimeslots = this.selectedTimeslots.filter((timeslot) => timeslot.id !== event.event.id);
        event.el.classList.remove('selected-event');
    }
    addSelectedEvent(event) {
        this.selectedTimeslots.push(parseToTimeslotModel(event));
        event.el.classList.add('selected-event');
    }
    get rosterTitle() {
        return this.rosterTemplate ? (this.newTitle = this.rosterTemplate.title) : '';
    }
    get weekStart() {
        const setting = this.$store.state.settings.find((setting) => {
            return setting.key === 'first_day_of_week';
        });
        if (setting) {
            this.hasWeekends = this.hasWeekendsInSchoolTimes()
                ? this.hasWeekendsInSchoolTimes()
                : ['sunday', 'saturday'].includes(setting.value);
            return dayMap[setting.value];
        }
        return 1;
    }
    get warningCount() {
        let count = 0;
        if (this.rosterTemplate && this.rosterTemplate.timeslots) {
            const timeslots = new Model().dataToModel({}, 'Timeslot', this.rosterTemplate.timeslots);
            timeslots.forEach((timeslot) => {
                if (!timeslot.is_valid) {
                    count += 1;
                }
            });
        }
        return count;
    }
    get businessHours() {
        if (!this.$store.state.openingHours) {
            return [];
        }
        return this.$store.state.openingHours;
    }
    get breadcrumbs() {
        return [
            {
                name: this.$tc('rosterTemplate.title', 2),
                path: '/roster/templates',
            },
            {
                name: this.rosterTemplate
                    ? this.rosterTemplate.title
                        ? this.rosterTemplate.title
                        : ''
                    : '',
                path: this.$route.path,
            },
        ];
    }
    get weekDays() {
        return [
            this.$t('weekDays.monday').toString(),
            this.$t('weekDays.tuesday').toString(),
            this.$t('weekDays.wednesday').toString(),
        ];
    }
    get calendarSettings() {
        return {
            ...defaultCalendarSettings,
            ...{
                header: false,
                columnHeaderFormat: {
                    weekday: 'short',
                },
                nowIndicator: false,
                selectOverlap: false,
                editable: false,
                eventOverlap: false,
                businessHours: businessHours(this.businessHours),
                firstDay: this.weekStart,
                weekends: this.hasWeekends,
                eventConstraint: 'businessHours',
                selectConstraint: 'businessHours',
                select: (selectionInfo) => {
                    this.createCalendarEvent(selectionInfo);
                },
                eventRender: (eventInfo) => {
                    if (!eventInfo.event.id) {
                        return true;
                    }
                    const type = this.renderType;
                    if (eventInfo.event.extendedProps.activities.length < 2) {
                        this.activitiesString = this.$tc('roster.activities', 1);
                    }
                    else {
                        this.activitiesString = this.$tc('roster.activities', 2);
                    }
                    return renderCustomTimeslot(eventInfo, type, this.getLockClass(eventInfo), this.activitiesString);
                },
                eventClick: (event) => {
                    if (this.isAddingMultipleEvents) {
                        this.toggleSelectedEvent(event);
                    }
                    else {
                        this.selectedEventId = event.event.id;
                        this.viewCalendarEvent(event.event);
                    }
                },
                eventDrop: (eventDropInfo) => {
                    const timeslot = parseToTimeslotModel(eventDropInfo.event, 'timeslot', this.rosterTemplate ? this.rosterTemplate.id : '');
                    this.updateTimeslot(timeslot);
                },
                eventResize: (eventResizeInfo) => {
                    const timeslot = parseToTimeslotModel(eventResizeInfo.event, 'timeslot', this.rosterTemplate ? this.rosterTemplate.id : '');
                    this.updateTimeslot(timeslot);
                },
            },
        };
    }
    get renderType() {
        if (this.users.length) {
            return 'users';
        }
        else if (this.rooms.length) {
            return 'rooms';
        }
        else if (this.levels.length) {
            return 'levels';
        }
        else if (this.groups.length) {
            return 'groups';
        }
        else if (this.areas.length) {
            return 'subjects';
        }
        else {
            return null;
        }
    }
    get getRosterTemplateFilter() {
        return {
            users: this.users,
            rooms: this.rooms,
            levels: this.levels,
            groups: this.groups,
            areas: this.areas,
        };
    }
    get UpdateRosterTemplate() {
        const rosterTemplate = this.rosterTemplate;
        return {
            title: rosterTemplate.title || this.$t('dialogOptions.confirmation').toString(),
            text: this.$tc('dialogOptions.update', 1, {
                item: this.$t('dialogOptions.item.template'),
            }).toString(),
            type: 'warning',
            buttons: {
                confirm: {
                    text: this.$t('dialogOptions.button.ok').toString(),
                    action: () => {
                        if (!rosterTemplate) {
                            return;
                        }
                        rosterTemplate
                            .update()
                            .then(() => {
                            this.$emit('updated', rosterTemplate);
                            this.rosterUpdated();
                        })
                            .catch((error) => {
                            ErrorHandler.network(error);
                        });
                    },
                },
                cancel: {
                    text: this.$t('dialogOptions.button.cancel').toString(),
                },
            },
        };
    }
    mounted() {
        this.fetchRosterTemplate();
        if (this.$route.query && !isEmpty(this.$route.query)) {
            this.$nextTick(() => {
                this.preSetFilters(this.$route.query);
                this.preFillSelects(this.$route.query);
                this.isLoading = true;
            });
            return;
        }
    }
    titleChanged() {
        if (this.newTitle) {
            this.oldTitleValue = this.newTitle;
        }
    }
    fetchRosterTemplate() {
        new RosterTemplateModel()
            .filter(this.getRosterTemplateFilter)
            .include([
            'timeslots',
            'activities',
            'groups',
            'users',
            'room',
            'levels',
            'areas',
            'days',
            'teacher',
        ])
            .find(this.$route.params.id)
            .then((rosterTemplate) => {
            this.rosterTemplate = rosterTemplate;
            const currentWeek = this.createWeek(DateTime.local());
            const timeslots = new Model().dataToModel({}, 'Timeslot', this.rosterTemplate.timeslots);
            this.events = parseTimeslotsToCalendarEvents(timeslots, currentWeek);
            this.isLoading = false;
            this.$nextTick(() => {
                window.dispatchEvent(new Event('resize'));
            });
        })
            .catch((error) => ErrorHandler.network(error));
    }
    preSetFilters(filters) {
        if (filters.areas) {
            this.areas = isArray(filters.areas) ? filters.areas : [filters.areas];
        }
        if (filters.rooms) {
            this.rooms = filters.rooms;
        }
        if (filters.levels) {
            this.levels = isArray(filters.levels) ? filters.levels : [filters.levels];
        }
        if (filters.groups) {
            this.groups = isArray(filters.groups) ? filters.groups : [filters.groups];
        }
        if (filters.users) {
            this.users = isArray(filters.users) ? filters.users : [filters.users];
        }
    }
    async preFillSelects(filters) {
        if (filters.areas && this.$refs.areaSelect) {
            this.preFillAreaSelect(isArray(filters.areas) ? filters.areas : [filters.areas]);
        }
        if (filters.rooms && this.$refs.roomsSelect) {
            this.preFillRoomsSelect(isArray(filters.rooms) ? filters.rooms : [filters.rooms]);
        }
        if (filters.levels && this.$refs.levelSelect) {
            this.preFillLevelSelect(isArray(filters.levels) ? filters.levels : [filters.levels]);
        }
        if (filters.groups && this.$refs.groupSelect) {
            this.preFillGroupSelect(isArray(filters.groups) ? filters.groups : [filters.groups]);
        }
        if (filters.users && this.$refs.usersSelect) {
            this.preFillUsersSelect(isArray(filters.users) ? filters.users : [filters.users]);
        }
    }
    async preFillRoomsSelect(roomIds) {
        const rooms = await new Room().filter('ids', roomIds).all();
        this.$refs.roomsSelect.setDefaultValue(rooms.map((room) => ({
            value: room.id,
            text: room.name,
        })), false);
    }
    async preFillAreaSelect(subjectIds) {
        const areas = await new Subject().filter('ids', subjectIds).all();
        this.$refs.areaSelect.setDefaultValue(areas.map((subject) => ({
            value: subject.id,
            text: subject.name,
        })), false);
    }
    async preFillLevelSelect(levelIds) {
        const levels = await new Level().filter('ids', levelIds).all();
        this.$refs.levelSelect.setDefaultValue(levels.map((level) => ({
            value: level.id,
            text: level.name,
        })), false);
    }
    async preFillGroupSelect(groupIds) {
        const groups = await new Group().filter('ids', groupIds).all();
        this.$refs.groupSelect.setDefaultValue(groups.map((group) => ({
            value: group.id,
            text: group.name,
        })), false);
    }
    async preFillUsersSelect(userIds) {
        const user = await new Group().filter('ids', userIds).all();
        this.$refs.usersSelect.setDefaultValue(user.map((user) => ({
            value: user.id,
            text: user.name,
        })), false);
    }
    createWeek(today) {
        const currentWeek = { 1: '', 2: '', 3: '', 4: '', 5: '', 6: '', 7: '' };
        let startDate = today;
        for (let i = 0; i < 7; i += 1) {
            const previousDay = today.minus({ days: i });
            const weekStart = this.weekStart === 0 ? 7 : this.weekStart;
            if (parseInt(previousDay.toFormat('c')) === weekStart) {
                startDate = previousDay;
                break;
            }
        }
        for (let j = 0; j < 7; j += 1) {
            const currentDate = startDate.plus({ days: j });
            const dayNumber = currentDate.toFormat('c');
            currentWeek[dayNumber] = currentDate.toFormat('yyyy-LL-dd');
        }
        return currentWeek;
    }
    hasWeekendsInSchoolTimes() {
        const saturday = this.$store.state.openingHours.find((hour) => {
            return hour.id === 'saturday';
        });
        const sunday = this.$store.state.openingHours.find((hour) => {
            return hour.id === 'sunday';
        });
        return ((sunday.starts_at !== '00:00:00' && sunday.ends_at !== '00:00:00') ||
            (saturday.starts_at !== '00:00:00' && saturday.ends_at !== '00:00:00'));
    }
    openDialog() {
        this.$store.dispatch('openDialog', this.UpdateRosterTemplate);
    }
    rosterUpdated() {
        this.isUpdated = true;
        setTimeout(() => {
            this.isUpdated = false;
        }, 3000);
    }
    createCalendarEvent(selectionInfo) {
        this.eventData = parseToTimeslotModel(selectionInfo);
        this.isAddingTimeslot = true;
    }
    viewCalendarEvent(selectionInfo) {
        this.isShowingActivitiesDialog = true;
        this.eventData = parseToTimeslotModel(selectionInfo);
        if (this.eventData) {
            this.eventData.template = this.rosterTemplate ? this.rosterTemplate.id : '';
        }
    }
    updateTimeslot(timeslot) {
        timeslot
            .update()
            .then(() => {
            this.fetchRosterTemplate();
        })
            .catch((error) => {
            ErrorHandler.network(error);
        });
    }
    refreshCalendar(timeslots) {
        const currentWeek = this.createWeek(DateTime.local());
        if (!currentWeek) {
            return;
        }
        this.fetchRosterTemplate();
    }
    getLockClass(eventInfo) {
        if (!eventInfo.event.extendedProps.is_valid) {
            return 'fc-needs_attention';
        }
        return '';
    }
    updateRosterTitle() {
        if (!this.rosterTemplate) {
            return;
        }
        this.rosterTemplate
            .update({
            title: this.newTitle,
        })
            .then(() => {
            this.$emit('titleUpdated');
            if (this.rosterTemplate) {
                this.$set(this.rosterTemplate, 'title', this.newTitle);
            }
            this.newTitle = '';
        })
            .catch((error) => {
            ErrorHandler.network(error);
        });
    }
    get snackbarActions() {
        return [
            {
                icon: 'more_time',
                click: () => {
                    this.isShowingCreateActivitiesDialog = true;
                },
                label: this.$tc('rosterTemplate.dialog.addActivity'),
            },
            {
                icon: 'post_add',
                click: () => {
                    this.isShowingAddBreakDialog = true;
                },
                label: this.$tc('rosterTemplate.dialog.addBreak'),
            },
        ];
    }
    get snackbarCount() {
        return this.selectedTimeslots.length;
    }
    get timeslots() {
        return this.rosterTemplate && this.rosterTemplate.timeslots
            ? this.rosterTemplate.timeslots
            : [];
    }
    get internalTimeslots() {
        if (this.isAddingMultipleEvents) {
            return this.selectedTimeslots;
        }
        return this.eventData;
    }
    cancelRosterTitle() {
        if (this.newTitle) {
            this.newTitle = this.oldTitleValue || '';
        }
    }
    resetFilters() {
        this.rooms = [];
        this.users = [];
        this.levels = [];
        this.groups = [];
        this.areas = [];
        this.clearSelects = true;
        this.isLoading = true;
        this.fetchRosterTemplate();
    }
    onCloseDialog(isOpen) {
        if (!isOpen) {
            this.isAddingMultipleEvents = false;
            if (!this.isAddingMultipleEvents) {
                this.clearSelectedTimeslots();
            }
        }
    }
    filterChanged(value) {
        if (value.length) {
            this.isLoading = true;
            this.fetchRosterTemplate();
        }
        const filters = { ...this.getRosterTemplateFilter };
        this.setQueryParams(filters);
    }
};
__decorate([
    Watch('isEditingTitle'),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], RosterTemplate.prototype, "titleChanged", null);
__decorate([
    Watch('isShowingCreateActivitiesDialog'),
    Watch('isShowingAddBreakDialog'),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Boolean]),
    __metadata("design:returntype", void 0)
], RosterTemplate.prototype, "onCloseDialog", null);
__decorate([
    Watch('rooms'),
    Watch('users'),
    Watch('groups'),
    Watch('levels'),
    Watch('areas'),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Array]),
    __metadata("design:returntype", void 0)
], RosterTemplate.prototype, "filterChanged", null);
RosterTemplate = __decorate([
    Component({
        components: {
            ScoolSnackbar,
            PageHeader,
            ShowActivitiesDialog,
            AddTimeslotDialog,
            LoaderCard,
            AsyncSelect,
            CreateActivityDialog,
            CreateRecessDialog,
        },
    })
], RosterTemplate);
export default RosterTemplate;
